// actions.ts — the chat control executor (LIGHT shell). The server brain returns a QUEUE of actions;
// the app executes them in order against the light local draft buffer + the SERVER. Posting goes
// through the server (/api/apps/oriora-social/post), which reads the customer's credential from the
// Connections hub and posts — the app NEVER holds a token. HITL: a post happens only on the user's
// explicit instruction (post_now is held for a Confirm tap in Chat.tsx — G17).

import { invoke } from '@tauri-apps/api/core';
import { getAccessToken } from '../supabase';
import { addDraft, updateDraft, removeDraft, getDraft, type PlatformId } from '../store/drafts';
import { runDraft, approve, reject, coachMe } from '../autopilot';

const API = (import.meta as any).env?.VITE_API_URL || 'https://api.orioralabs.com';

export type ChatAction =
  | { kind: 'none' }
  | { kind: 'draft'; platform: PlatformId; content: string; topic?: string | null; ai_disclosure?: boolean }
  | { kind: 'edit'; draft_id: string; content: string }
  | { kind: 'post_now'; draft_id: string }
  | { kind: 'discard'; draft_id: string }
  | { kind: 'connect_account'; platform: PlatformId }
  | { kind: 'schedule'; text: string; platform: PlatformId; scheduled_at: string }
  | { kind: 'remember'; text: string }
  // G16 — the flagship pipeline is chat-drivable too (same endpoints the dashboard buttons call):
  | { kind: 'run_autopilot_draft' }
  | { kind: 'approve_queue'; item_id: string }
  | { kind: 'reject_queue'; item_id: string }
  | { kind: 'coach' };

export interface ActionResult {
  ok: boolean;
  message: string;  // shown back in the chat
  changed?: boolean; // the draft list / queue changed → the UI should refresh
}

async function postViaServer(platform: PlatformId, text: string): Promise<{ ok: boolean; url?: string; error?: string }> {
  const token = await getAccessToken();
  if (!token) return { ok: false, error: 'Not signed in.' };
  const r = await fetch(`${API}/api/apps/oriora-social/post`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ service: platform, text }),
  });
  const j: any = await r.json().catch(() => ({}));
  if (!r.ok) return { ok: false, error: j.message || j.error || `post failed (${r.status})` };
  return j.success ? { ok: true, url: j.post_url } : { ok: false, error: j.error || 'post failed' };
}

export async function executeAction(a: ChatAction): Promise<ActionResult> {
  try {
    switch (a.kind) {
      case 'none':
        return { ok: true, message: '' };

      case 'draft': {
        const d = addDraft({ platform: a.platform, content: a.content, topic: a.topic ?? null, ai_disclosure: a.ai_disclosure });
        return { ok: true, message: `Drafted for ${a.platform}. Say "post it" when you're happy with it.\n\n"${d.content}"`, changed: true };
      }

      case 'edit':
        updateDraft(a.draft_id, { content: a.content });
        return { ok: true, message: 'Updated the draft.', changed: true };

      case 'post_now': {
        const d = getDraft(a.draft_id);
        if (!d) return { ok: false, message: "Couldn't find that draft." };
        const text = d.ai_disclosure ? `${d.content}\n\n🤖 AI-assisted` : d.content;
        const res = await postViaServer(d.platform, text);
        if (res.ok) {
          updateDraft(d.id, { status: 'posted', post_url: res.url });
          return { ok: true, message: `Posted. ✅ ${res.url ?? ''}`.trim(), changed: true };
        }
        updateDraft(d.id, { status: 'failed', error: res.error });
        return { ok: false, message: `Couldn't post: ${res.error}`, changed: true };
      }

      case 'discard':
        removeDraft(a.draft_id);
        return { ok: true, message: 'Discarded.', changed: true };

      case 'connect_account':
        // Connections are managed in the Oriora ACCOUNT on the website (the hub), never in the app.
        invoke('open_external', { url: 'https://orioralabs.com/settings?tab=connections' }).catch(() => {});
        return { ok: true, message: `Opening your Oriora account to connect ${a.platform} — add it there, then come back and we'll post.` };

      case 'schedule': {
        // The server lands this as PENDING in the review queue (not auto-scheduled) — a chat turn can
        // carry injected text, so the brain's decision alone never posts. The user approves it in the
        // dashboard (where the proposed time is pre-filled), then it schedules.
        const when = a.scheduled_at ? new Date(a.scheduled_at).toLocaleString() : 'the chosen time';
        return { ok: true, message: `Added to your review queue (for ${when} on ${a.platform}). Open the dashboard to approve & schedule it.`, changed: true };
      }

      case 'remember':
        // The server already wrote this to the customer's cloud memory; nothing for the client to do.
        return { ok: true, message: '' };

      case 'run_autopilot_draft': {
        const item = await runDraft();
        return { ok: true, message: `Drafted from your sources — it's in Needs review:\n\n"${item.draft}"`, changed: true };
      }

      case 'approve_queue': {
        const item = await approve(a.item_id);
        return { ok: true, message: `Approved ✓ — post it now or schedule it from the dashboard.\n\n"${String(item.draft).slice(0, 120)}"`, changed: true };
      }

      case 'reject_queue': {
        await reject(a.item_id);
        return { ok: true, message: 'Rejected — I\'ll steer future drafts away from that.', changed: true };
      }

      case 'coach': {
        const r = await coachMe();
        return { ok: true, message: r.coaching, changed: false };
      }

      default:
        return { ok: false, message: "I didn't catch that — try again?" };
    }
  } catch (e) {
    return { ok: false, message: `That didn't work: ${String(e)}` };
  }
}
